import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { QCard, QCardContent, QTextField, QTypography, QButton } from '@kw/quokka-ui';
import { SettingsMode } from './enum/SettingsMode.enum';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useOrganization } from './SettingsDropDownMenu';
import { sendHttpRequest } from '../../utils/network.service';
import { validateEmail } from '../../utils/emailValidation';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';

interface OrganizationSettingsCardProps {
  isAdmin: boolean;
  mode: SettingsMode;
  orgData: { [key: string]: string };
  setMode: React.Dispatch<React.SetStateAction<SettingsMode>>;
  supportEmail: string;
  setSupportEmail: React.Dispatch<React.SetStateAction<string>>;
  resetInputValues: () => void;
  emailInputError: string;
  setEmailInputError: React.Dispatch<React.SetStateAction<string>>;
}

const OrganizationSettingsCard = ({
  isAdmin,
  mode,
  orgData,
  setMode,
  supportEmail,
  setSupportEmail,
  resetInputValues,
  emailInputError,
  setEmailInputError,
}: OrganizationSettingsCardProps) => {
  const isEditMode = mode === SettingsMode.EDIT;
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (supportEmail.length) {
      setIsSaveButtonDisabled(false);
    }
    if (supportEmail.length === 0) {
      setIsSaveButtonDisabled(true);
    }
  }, [supportEmail]);

  const updateSupportEmail = async () => {
    if (validateEmail(supportEmail)) {
      const body = { supportEmail };
      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
        method: 'PUT',
        body,
        successMessage: 'Successfully updated Q-Scout support email',
        errorMessage: 'Error updating Q-Scout support email',
        enqueueSnackbar,
      });
      resetInputValues();
    } else {
      setEmailInputError('Please enter a valid email address');
    }
  };

  if (!verifyAuthPermission(AuthPermissions.SETTINGS_SUPPORT_EMAIL)) return null;

  return (
    <StyledQCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold">Support Email</StyledQTypography>
        <StyledQTypography variant="bodyRegular">
          Enter the email address where Q-Scout in-app support requests should be sent.
        </StyledQTypography>
        <StyledQTypography variant="h5SemiBold">Email</StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <QTextField
              placeholder={supportEmail.length ? supportEmail : orgData.supportEmail}
              onChange={e => {
                setSupportEmail(e.target.value);
              }}
              inputProps={{ 'aria-label': 'supportEmail' }}
            />
            {emailInputError && (
              <StyledErrorMessageWrapper>
                <KwFormErrorMessage key={emailInputError} error={Boolean(emailInputError)} reason={emailInputError} />
              </StyledErrorMessageWrapper>
            )}
            <StyledButtonWrapper>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" disabled={isSaveButtonDisabled} onClick={updateSupportEmail}>
                Save
              </QButton>
            </StyledButtonWrapper>
          </>
        ) : (
          <>
            <StyledQTypography>{orgData.supportEmail}</StyledQTypography>
            <StyledButtonWrapper>
              <QButton variant="filled" onClick={() => setMode(SettingsMode.EDIT)}>
                Edit
              </QButton>
            </StyledButtonWrapper>
          </>
        )}
      </QCardContent>
    </StyledQCard>
  );
};

export function OrganizationSettings() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [mode, setMode] = useState(SettingsMode.READ);
  const [supportEmail, setSupportEmail] = useState('');
  const [emailInputError, setEmailInputError] = useState('');

  const canUpdateOrg = verifyAuthPermission(AuthPermissions.UPDATE_ORG);
  const { orgData, organizationError, mutate } = useOrganization(accessToken);

  const resetInputValues = () => {
    setSupportEmail('');
    setMode(SettingsMode.READ);
    setEmailInputError('');
    mutate();
  };

  if (organizationError) return <div>failed to load</div>;

  return (
    <OrganizationSettingsCard
      isAdmin={canUpdateOrg}
      mode={mode}
      orgData={orgData}
      setMode={setMode}
      supportEmail={supportEmail}
      setSupportEmail={setSupportEmail}
      resetInputValues={resetInputValues}
      emailInputError={emailInputError}
      setEmailInputError={setEmailInputError}
    />
  );
}

const StyledQCard = styled(QCard)`
  width: 100%;
  min-width: 300px;
  max-width: 400px;
`;

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;

const StyledButtonWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  column-gap: 14px;
  margin-top: 24px;
`;

const StyledErrorMessageWrapper = styled('div')`
  margin: 14px 0;
`;
